/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import { useCallback, useEffect, useRef } from "react";

/**
 * מציב את הסמן בשדה שממתינים בו - ברגע שהוא נהיה השדה הזה.
 *
 * במסכי הקוד שדה ההקלדה הוא כל מה שנותר לעשות במסך, ובכל זאת הלקוח היה צריך ללחוץ
 * עליו לפני שיוכל להקיש. במסכים שבהם השדה נפתח במעבר שלב - "שליחת קוד" ואז הקלדה
 * באותו מסך - השדה נפתח בדיוק מתחת לאצבע שלחצה, ובלי סמן.
 *
 * `key` הוא **מה שהשדה ממתין לו**, ולא דגל: ריק = אין עכשיו שדה שצריך את הסמן
 * (שלב אחר, שיטה שאינה מוקלדת), וכל שינוי לערך שאינו ריק מחזיר את הסמן. ברירת
 * המחדל מתאימה למסך שכולו הקלדת קוד - שם די בפוקוס בטעינה.
 *
 * **פוקוס במובייל פותח את המקלדת**, ולכן אין לקרוא לזה במסך שיש בו מה לראות מעל
 * השדה - קוד QR, למשל - שהמקלדת תכסה.
 *
 * מחזיר `[ref, focus]`:
 *   ref   – להצבה על השדה
 *   focus – החזרת הסמן ביד, לפעולה שאינה משנה את `key`: שליחה חוזרת מקישה קוד חדש
 *           לאותו שדה, ומי שלחץ על הקישור נמצא עם הסמן בתחתית המסך. בטוח לקריאה
 *           גם כשהשדה עדיין לא נטען - אז אין מה למקד, וה-`key` שמשתנה ימקד אותו.
 */
export function useAutoFocus(key = true) {
  const ref = useRef(null);

  const focus = useCallback(() => {
    if (!ref.current) return;
    ref.current.focus();
    /*
     * ובחירת מה שכבר מוקלד - כי בשליחה חוזרת הקוד הקודם נשאר בשדה, וסמן שמגיע
     * לסופו מזמין את הלקוח להקיש קוד חדש בהמשך לקוד שפג. אותה תבנית כמו בתיבות
     * קוד הגיבוי, שבוחרות את תוכנן בכל פוקוס.
     */
    ref.current.select?.();
  }, []);

  useEffect(() => {
    if (!key) return;
    focus();
  }, [key, focus]);

  return [ref, focus];
}
